import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { formatDate, API_URL } from '../lib/utils';
import { CheckCircle, Clock, Loader2 } from 'lucide-react';

const QuoteResultPage = () => {
  const { quoteId } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const response = await fetch(`${API_URL}/quotes/${quoteId}`);
        if (!response.ok) throw new Error('Quote not found');
        const data = await response.json();
        setQuote(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchQuote();
  }, [quoteId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-[#1a365d]" />
        </div>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="content-section text-center">
          <Clock className="h-16 w-16 text-[var(--gold)] mx-auto mb-4" />
          <h2>Quote Not Found</h2>
          <p className="mb-6">{error}</p>
          <Link to="/quote" className="btn-navy">Request a Quote</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="page-banner">
        <div className="container-wide">
          <h1>QUOTE REQUEST SUBMITTED</h1>
          <div className="breadcrumb"><Link to="/">Home</Link> &gt; <Link to="/quote">Quote</Link> &gt; Confirmation</div>
        </div>
      </div>

      <section className="content-section" data-testid="quote-result-content">
        <div className="container-narrow">
          
          {/* Success Header */}
          <div className="text-center mb-8">
            <CheckCircle className="h-20 w-20 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-serif text-[#1a365d] mb-2">Thank You!</h2>
            <p className="text-lg text-gray-600">Your quote request has been submitted successfully.</p>
            <p className="text-sm text-gray-500 mt-2">Reference #{quote.quote_number}</p>
          </div>

          <hr className="content-divider" />

          {/* What's Next */}
          <div className="bg-[#f8f9fa] border border-gray-200 rounded-lg p-6 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Clock className="h-6 w-6 text-[var(--gold)]" />
              <h3 className="text-xl font-serif text-[#1a365d]">What Happens Next?</h3>
            </div>
            <ol className="space-y-3 text-gray-700">
              <li className="flex gap-3">
                <span className="font-bold text-[#1a365d]">1.</span>
                <span>Our insurance specialists will review your information.</span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-[#1a365d]">2.</span>
                <span>We'll prepare a personalized quote tailored to your needs.</span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-[#1a365d]">3.</span>
                <span><strong>We'll contact you within 3 business days</strong> with your customized quote.</span>
              </li>
            </ol>
          </div>

          <hr className="content-divider" />

          {/* Request Summary */}
          <h3 className="text-lg font-serif text-[#1a365d] mb-4">Request Summary</h3>
          <div className="grid grid-cols-2 gap-4 mb-8 text-sm">
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-gray-500">Coverage Type</p>
              <p className="font-semibold text-[#1a365d] capitalize">{quote.quote_type} Insurance</p>
            </div>
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-gray-500">Submitted</p>
              <p className="font-semibold">{formatDate(quote.created_at)}</p>
            </div>
            {quote.request_data?.email && (
              <div className="bg-gray-50 p-3 rounded col-span-2">
                <p className="text-gray-500">Contact Email</p>
                <p className="font-semibold">{quote.request_data.email}</p>
              </div>
            )}
          </div>

          <hr className="content-divider" />

          {/* CTA */}
          <div className="text-center">
            <p className="text-gray-600 mb-6">
              Have questions in the meantime? We're here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact-business-insurance" className="btn-navy" data-testid="contact-btn">Contact Us</Link>
              <Link to="/specializations" className="btn-outline-navy" data-testid="browse-services-btn">Browse Services</Link>
            </div>
            <div className="mt-6">
              <Link to="/quote" className="text-[#1a365d] font-semibold hover:text-[var(--gold)]">Submit Another Quote Request →</Link>
            </div>
          </div>

          <hr className="content-divider" />

          <p className="text-center text-gray-600">
            Questions? Contact <a href="mailto:monte@archoninsurance.com" className="text-[var(--gold)] hover:underline">monte@archoninsurance.com</a>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default QuoteResultPage;
